import axios from 'axios';

import { Constants } from '@/constants';

export const MAX_CIGAR_MATCH_IMAGES = 4;

const getImagePayload = (imageUrls: string[]) => {
  const limitedImageUrls = imageUrls.slice(0, MAX_CIGAR_MATCH_IMAGES);
  return limitedImageUrls.length === 1
    ? { imageUrl: limitedImageUrls[0] }
    : { imageUrls: limitedImageUrls };
};

export interface GenerateEmbeddingsRequest {
  maximumImages?: number;
  objectKeys?: string[];
  imageUrls?: string[];
  sourceType?:
    | 'training'
    | 'manual_match'
    | 'confirmed_scan'
    | 'smoke_session'
    | 'catalog'
    | 'user_confirmed';
  verified?: boolean;
  force?: boolean;
}

export interface EmbeddingReference {
  id: string;
  source_image_id: string;
  embedding_view: string;
}

export interface GenerateEmbeddingsResponse {
  success: boolean;
  cigar_id: number;
  generated: number;
  embedding_rows_generated?: number;
  skipped: number;
  references: EmbeddingReference[];
  model: string;
  version: string;
}

export interface GenerateFingerprintsRequest {
  force?: boolean;
  maximumImages?: number;
}

export interface GenerateFingerprintsResponse {
  success?: boolean;
  cigar_id?: number;
  generated?: number;
  skipped?: number;
  fingerprint_count?: number;
  message?: string;

  [field: string]: unknown;
}

export interface StartFingerprintJobRequest
  extends GenerateFingerprintsRequest {
  limit?: number;
}

export interface FingerprintJobResponse {
  success?: boolean;
  job?: {
    id?: string;
    status?: string;
    [field: string]: unknown;
  };
  message?: string;

  [field: string]: unknown;
}

export interface SimilarityCandidate {
  cigar_id: number;
  confidence: number;
  similarity: number;
  supporting_references: number;
  best_similarity: number;
  mean_reference_similarity: number;
  supporting_images: number;
  matched_views: number;
}

export interface SimilarityThresholds {
  suggestion_similarity: number;
  match_similarity: number;
  match_margin: number;
  reference_support_similarity: number;
  minimum_supporting_references: number;
}

export interface SimilarityMetadata {
  model: string;
  version: string;
  score_type: 'cosine_similarity';
  image_count: number;
  embedding_view_count: number;
  thresholds: SimilarityThresholds;
}

export interface FindSimilarResponse {
  success: boolean;
  matched: boolean;
  decisionReason?: string;
  decision_margin?: number;
  candidates: SimilarityCandidate[];
  metadata?: SimilarityMetadata;
}

export interface HydratedCigarCandidate {
  cigarName?: string;
  cigarImageUrl?: string;
  cigarBandImageUrl?: string;
}

export interface HydratedSimilarityCandidate
  extends SimilarityCandidate,
    HydratedCigarCandidate {}

export interface IdentifyCigarRequest {
  prompt?: string;
  suggestionLimit?: number;
  stageResults?: Record<string, unknown>;
  researchMode?: IdentifyResearchMode;
  researchProfile?: IdentifyResearchProfile;
}

export type IdentifyResearchMode = 'auto' | 'open_world' | 'catalog_guided';
export type IdentifyResearchProfile = 'quick' | 'thorough';

export interface IdentificationCandidate {
  cigar_id: number;
  confidence: number;
  similarity?: number;
  supporting_references?: number;
}

export type IdentificationStatus = 'matched' | 'suggestions' | 'unmatched';
export type IdentificationSource =
  | 'embedding'
  | 'multimodal'
  | 'combined'
  | 'none';

export interface TokenUsageMetadata {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
}

export interface IdentificationMetadata {
  source: IdentificationSource;
  processing_ms: number;
  image_count: number;
  candidate_count: number;
  embedding_resolved: boolean;
  gpt_fallback_used: boolean;
  embedding_model?: string;
  embedding_version?: string;
  multimodal_model?: string;
  token_usage?: TokenUsageMetadata;
  decision_reason?: string;
}

export interface IdentifyCigarResponse {
  success: boolean;
  status: IdentificationStatus;
  match?: IdentificationCandidate;
  suggestions?: IdentificationCandidate[];
  metadata: IdentificationMetadata;
}

export interface HydratedIdentificationCandidate
  extends IdentificationCandidate,
    HydratedCigarCandidate {
  resultType: 'match' | 'suggestion';
}

export interface EvaluateCigarMatcherRequest {
  cigarIds?: number[];
  maximumImages?: number;
  maximumPerCigar?: number;
  includeDetails?: boolean;
}

export interface EvaluationDistribution {
  minimum: number;
  average: number;
  p50: number;
  p95: number;
  maximum: number;
}

export interface EvaluationDetail {
  source_image_id: string;
  expected_cigar_id: number;
  predicted_cigar_ids: number[];
  confidence: number;
  similarity: number;
  runner_up_similarity: number;
  margin: number;
  auto_matched: boolean;
  decision_reason: string;
  latency_ms: number;
}

export interface EvaluateCigarMatcherResponse {
  success: boolean;
  dataset: string;
  image_count: number;
  cigar_count: number;
  accuracy: {
    top_1: number;
    top_3: number;
    top_5: number;
  };
  automation: {
    coverage: number;
    false_positive_count: number;
    false_positive_rate: number;
  };
  confidence: EvaluationDistribution;
  similarity: EvaluationDistribution;
  margin: EvaluationDistribution;
  latency_ms: EvaluationDistribution;
  details?: EvaluationDetail[];
}

export type EmbeddingJobMode = 'missing' | 'regenerate_all';

export type EmbeddingJobStatus =
  | 'queued'
  | 'discovering'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'cancelled'
  | 'failed';

export type EmbeddingJobFailureCode =
  | 'queue_dispatch_timeout'
  | 'discovery_timeout'
  | 'worker_inactive_timeout'
  | (string & {});

export interface EmbeddingJobError {
  id: string;
  source_image_id: string;
  source_entity_type: 'cigar_image' | 'vitola';
  source_record_id: number;
  cigar_id: number;
  image_url: string;
  attempt_count: number;
  error_code: string | null;
  error_detail: string | null;
}

export type EmbeddingJobItemStatus =
  | 'pending'
  | 'processing'
  | 'embedded'
  | 'skipped'
  | 'failed';

export interface EmbeddingJobActivityItem {
  id: string;
  status: EmbeddingJobItemStatus;
  cigar_id: number;
  source_image_id: string;
  source_entity_type: 'cigar_image' | 'vitola';
  source_record_id: number;
  image_url: string;
  attempt_count: number;
  error_code: string | null;
  error_detail: string | null;
  processing_ms: number | null;
  preparation_outcome: 'trimmed' | 'unchanged' | null;
  completed_at: string | null;
  updated_at: string;
}

export interface EmbeddingJobActivity {
  active: boolean;
  remaining: number;
  last_activity_at: string;
  recent_items: EmbeddingJobActivityItem[];
}

export interface EmbeddingJob {
  id: string;
  mode: EmbeddingJobMode;
  status: EmbeddingJobStatus;
  dry_run: boolean;
  item_limit: number | null;
  embedding_model: string;
  embedding_version: string;
  preparation_version: string;
  discovered: number;
  processed: number;
  embedded: number;
  skipped: number;
  failed: number;
  source_counts: Record<string, number>;
  normalization_counts: Record<string, number>;
  total_processing_ms: number;
  average_processing_ms: number;
  started_at: string | null;
  completed_at: string | null;
  reconciled_at: string | null;
  created_at: string;
  updated_at: string;
  failure_code: EmbeddingJobFailureCode | null;
  failure_detail: string | null;
  errors?: EmbeddingJobError[];
  activity?: EmbeddingJobActivity;
}

export interface EmbeddingCoverageRequest {
  cigarIds: number[];
}

export interface CigarEmbeddingCoverage {
  cigar_id: number;
  has_embeddings: boolean;
  reference_image_count: number;
  embedding_count: number;
  source_counts: Record<string, number>;
  last_updated_at: string | null;
}

export interface EmbeddingCoverageResponse {
  success: true;
  model: string;
  version: string;
  cigars: CigarEmbeddingCoverage[];
}

export interface StartEmbeddingJobRequest {
  mode?: EmbeddingJobMode;
  dryRun?: boolean;
  limit?: number;
  confirmRegenerateAll?: boolean;
}

export interface EmbeddingJobResponse {
  success: boolean;
  job: EmbeddingJob;
}

export interface EmbeddingJobListResponse {
  success: boolean;
  jobs: EmbeddingJob[];
}

export interface RetryEmbeddingJobFailuresResponse
  extends EmbeddingJobResponse {
  retried: number;
}

export interface CancelEmbeddingJobResponse extends EmbeddingJobResponse {
  success: true;
  cancelled: boolean;
}

export type EmbeddingRuntimeLogLevel = 'info' | 'warn' | 'error';

export interface EmbeddingRuntimeLogEvent {
  id: string;
  timestamp: string;
  level: EmbeddingRuntimeLogLevel;
  message: string;
}

export interface EmbeddingRuntimeLogsRequest {
  limit?: number;
  lookbackMinutes?: number;
}

export interface EmbeddingRuntimeLogsResponse {
  success: true;
  source: 'cloudwatch';
  available: boolean;
  job_id: string;
  from: string;
  to: string;
  events: EmbeddingRuntimeLogEvent[];
  truncated: boolean;
  poll_after_ms: number;
}

export const ACTIVE_EMBEDDING_JOB_STATUSES: EmbeddingJobStatus[] = [
  'queued',
  'discovering',
  'running',
];

export const TERMINAL_EMBEDDING_JOB_STATUSES: EmbeddingJobStatus[] = [
  'completed',
  'completed_with_errors',
  'cancelled',
  'failed',
];

export const isEmbeddingJobActive = (job?: EmbeddingJob | null) =>
  Boolean(
    job &&
      ACTIVE_EMBEDDING_JOB_STATUSES.includes(job.status as EmbeddingJobStatus)
  );

export const isEmbeddingJobTerminal = (job?: EmbeddingJob | null) =>
  Boolean(
    job &&
      TERMINAL_EMBEDDING_JOB_STATUSES.includes(job.status as EmbeddingJobStatus)
  );

export const getEmbeddingSuccessMessage = (
  result: Pick<
    GenerateEmbeddingsResponse,
    'generated' | 'embedding_rows_generated' | 'skipped'
  > &
    Partial<Pick<GenerateEmbeddingsResponse, 'model' | 'version'>>
) => {
  let message;
  if (result.generated === 0 && result.skipped > 0) {
    message = 'All reference images already have embeddings.';
  } else if (result.generated === 0 && result.skipped === 0) {
    message = 'No training images were found for this cigar.';
  } else if (result.embedding_rows_generated !== undefined) {
    message = `Processed ${result.generated} source photos into ${result.embedding_rows_generated} deterministic view embeddings; skipped ${result.skipped} existing source photos.`;
  } else {
    message = `Generated ${result.generated} embeddings; skipped ${result.skipped} existing references.`;
  }

  const modelVersion = [result.model, result.version]
    .filter(Boolean)
    .join(' · ');
  return modelVersion ? `${message} ${modelVersion}` : message;
};

export const generateCigarEmbeddings = async (
  cigarId: string | number,
  request: GenerateEmbeddingsRequest = { maximumImages: 10 }
) => {
  const response = await axios.post<GenerateEmbeddingsResponse>(
    `${Constants.aiPath}/admin/cigar-matching/cigars/${cigarId}/embeddings`,
    request
  );

  return response.data;
};

export const generateCigarFingerprints = async (
  cigarId: string | number,
  request: GenerateFingerprintsRequest = {
    force: false,
    maximumImages: 3,
  }
) => {
  const response = await axios.post<GenerateFingerprintsResponse>(
    `${Constants.aiPath}/admin/cigar-matching/cigars/${cigarId}/fingerprints`,
    request
  );

  return response.data;
};

export const startCigarFingerprintJob = async (
  request: StartFingerprintJobRequest = {
    force: false,
    maximumImages: 3,
  }
) => {
  const response = await axios.post<FingerprintJobResponse>(
    `${Constants.aiPath}/admin/cigar-matching/fingerprint-jobs`,
    request
  );

  return response.data;
};

export const findSimilarCigars = async (imageUrls: string[]) => {
  const response = await axios.post<FindSimilarResponse>(
    `${Constants.aiPath}/admin/cigar-matching/similar`,
    getImagePayload(imageUrls)
  );

  return response.data;
};

export const identifyCigar = async (
  imageUrls: string[],
  request: IdentifyCigarRequest = {}
) => {
  const response = await axios.post<IdentifyCigarResponse>(
    `${Constants.aiPath}/admin/cigar-matching/identify`,
    { ...getImagePayload(imageUrls), ...request }
  );

  return response.data;
};

export type RecognitionStageResponse = Record<string, any>;

export interface AnalyzeWrapperRequest {
  imageUrl: string;
  candidates: Array<Record<string, unknown>>;
}

export interface EnrichCigarRequest {
  sourceUrl?: string;
  cigar: {
    brand: string | null;
    name: string | null;
    line: string | null;
    aliases: string[];
    countryOfOrigin: string | null;
    wrapper: string | string[] | null;
    binder: string | string[] | null;
    filler: string | string[] | null;
    strength: string | number | null;
  };
}

const postRecognitionStage = async (
  path: string,
  request: Record<string, unknown>
) => {
  const response = await axios.post<RecognitionStageResponse>(
    `${Constants.aiPath}/cigars/${path}`,
    request
  );
  return response.data;
};

export const preprocessCigarImage = (imageUrl: string) =>
  postRecognitionStage('preprocess', { imageUrl });

export const findSimilarCigarsDebug = (imageUrl: string) =>
  postRecognitionStage('similar', { imageUrl });

export const recognizeCigarText = (imageUrl: string) =>
  postRecognitionStage('text/recognize', { imageUrl });

export const fingerprintCigarImage = (imageUrl: string) =>
  postRecognitionStage('fingerprint', { imageUrl });

export const recognizeCigarFingerprint = (
  fingerprint: Record<string, unknown>,
  limit = 10
) => postRecognitionStage('fingerprint/recognize', { fingerprint, limit });

export const identifyCigarDebug = (
  imageUrl: string,
  stageResults: Record<string, unknown> = {},
  researchMode: IdentifyResearchMode = 'auto',
  researchProfile: IdentifyResearchProfile = 'quick'
) =>
  postRecognitionStage('identify', {
    imageUrl,
    suggestionLimit: 5,
    researchMode,
    researchProfile,
    stageResults,
  });

export const enrichCigarHypothesis = (request: EnrichCigarRequest) =>
  postRecognitionStage('enrich', request);

export const analyzeCigarWrapper = async (request: AnalyzeWrapperRequest) => {
  const response = await axios.post<RecognitionStageResponse>(
    `${Constants.aiPath}/cigars/wrapper/analyze`,
    request
  );
  return response.data;
};

export const evaluateCigarMatcher = async (
  request: EvaluateCigarMatcherRequest
) => {
  const response = await axios.post<EvaluateCigarMatcherResponse>(
    `${Constants.aiPath}/admin/cigar-matching/evaluate`,
    request
  );

  return response.data;
};

export const startCigarEmbeddingJob = async (
  input: StartEmbeddingJobRequest = {}
) => {
  const mode = input.mode || 'missing';
  if (mode === 'regenerate_all' && input.confirmRegenerateAll !== true) {
    throw new Error('Full regeneration requires explicit confirmation.');
  }

  const request: StartEmbeddingJobRequest =
    mode === 'regenerate_all'
      ? { mode, confirmRegenerateAll: true }
      : {
          mode,
          dryRun: input.dryRun ?? false,
          ...(input.limit !== undefined ? { limit: input.limit } : {}),
        };
  const response = await axios.post<EmbeddingJobResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs`,
    request
  );

  return response.data;
};

export const getCigarEmbeddingJob = async (jobId: string) => {
  const response = await axios.get<EmbeddingJobResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs/${jobId}`
  );

  return response.data;
};

export const listCigarEmbeddingJobs = async (limit = 20) => {
  const response = await axios.get<EmbeddingJobListResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs`,
    { params: { limit } }
  );

  return response.data;
};

export const retryCigarEmbeddingJobFailures = async (jobId: string) => {
  const response = await axios.post<RetryEmbeddingJobFailuresResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs/${jobId}/retry-failures`
  );

  return response.data;
};

export const cancelCigarEmbeddingJob = async (jobId: string) => {
  const response = await axios.post<CancelEmbeddingJobResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs/${jobId}/cancel`
  );

  return response.data;
};

export const getCigarEmbeddingJobRuntimeLogs = async (
  jobId: string,
  request: EmbeddingRuntimeLogsRequest = {}
) => {
  const response = await axios.get<EmbeddingRuntimeLogsResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-jobs/${jobId}/runtime-logs`,
    {
      params: {
        limit: request.limit ?? 100,
        lookbackMinutes: request.lookbackMinutes ?? 30,
      },
    }
  );

  return response.data;
};

export const getCigarEmbeddingCoverage = async (cigarIds: number[]) => {
  const response = await axios.post<EmbeddingCoverageResponse>(
    `${Constants.aiPath}/admin/cigar-matching/embedding-coverage`,
    { cigarIds }
  );

  return response.data;
};

export const hydrateCigarCandidates = async <
  Candidate extends { cigar_id: number },
>(
  candidates: Candidate[],
  limit = 5
): Promise<Array<Candidate & HydratedCigarCandidate>> =>
  Promise.all(
    candidates.slice(0, limit).map(async (candidate) => {
      try {
        const response = await axios.get(
          `${Constants.clientPath}/cigars/${candidate.cigar_id}`
        );
        return {
          ...candidate,
          cigarName:
            response.data?.full_name ||
            [
              response.data?.brand?.name || response.data?.brand,
              response.data?.name,
            ]
              .filter(Boolean)
              .join(' '),
          cigarBandImageUrl:
            response.data?.bandImageUrl || response.data?.band_image_url,
          cigarImageUrl:
            response.data?.bandImageUrl ||
            response.data?.band_image_url ||
            response.data?.displayImageUrl ||
            response.data?.display_image_url ||
            response.data?.image_url,
        };
      } catch {
        return candidate;
      }
    })
  );

export const hydrateSimilarityCandidates = async (
  candidates: SimilarityCandidate[]
): Promise<HydratedSimilarityCandidate[]> => hydrateCigarCandidates(candidates);
